import axios from "axios";
import { format } from "date-fns";

import { AgentCurrentTicketsFilters } from "./AgentCurrentTicketsFilters";
import { LoadReport } from "./LoadReport";
import { ReportsFilters } from "./ReportsFilters";
import { PaginationOptions } from "../../common/types/Pagination";
import { inWorkStatusesIds, closedStatusesIds } from "../ticketStatuses/TicketStatus";
import { emptyPaginationDetails, mapApiPaginationDetails, mapToApiPagination } from "../../common/api/paginationHelper";
import { TicketReport } from "./TicketReport";
import { getTicketPrettyId } from "../tickets/ticketsApi";
import { convertISOStringToDate } from "../../common/utils/dateHelper";
import { AgentTicketsReportSummary } from "./AgentTicketsReportSummary";

const REPORTS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/reports`;

export function formatMinutesToTimeString(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  if (hours === 0 && minutes === 0) {
    return "0m";
  }
  if (hours === 0) {
    return `${minutes}m`;
  }
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}

export async function fetchLoadReport(reportsFilters: ReportsFilters) {
  const response = await axios.post(`${REPORTS_API_URL}/summary`, mapFiltersToApi(reportsFilters));
  const loadReport: LoadReport = response.data;
  return loadReport;
}

export async function fetchTicketsReport(reportsFilters: ReportsFilters, paginationOptions: PaginationOptions) {
  try {
    const response = await axios.post(`${REPORTS_API_URL}/tickets`, {
      ...mapFiltersToApi(reportsFilters),
      ...mapToApiPagination(paginationOptions),
    });
    const pagination = mapApiPaginationDetails(response.data);
    const ticketsReport: TicketReport[] = response.data.items.map(mapApiTicketReport);
    return {
      pagination,
      ticketsReport,
    };
  } catch (err) {
    return {
      pagination: emptyPaginationDetails,
      ticketsReport: [],
    };
  }
}

export async function fetchAgentTicketsSummary(filters: AgentCurrentTicketsFilters) {
  const response = await axios.post(`${REPORTS_API_URL}/agent-summary`, mapAgentFiltersToApi(filters));
  return response.data as AgentTicketsReportSummary;
}

export async function fetchAgentTicketsReport(filters: AgentCurrentTicketsFilters, paginationOptions: PaginationOptions) {
  try {
    const response = await axios.post(`${REPORTS_API_URL}/agent-tickets`, {
      ...mapAgentFiltersToApi(filters),
      ...mapToApiPagination(paginationOptions),
    });
    const pagination = mapApiPaginationDetails(response.data);
    const ticketsReport: TicketReport[] = response.data.items.map(mapApiTicketReport);
    return { pagination, ticketsReport };
  } catch (err) {
    return { pagination: emptyPaginationDetails, ticketsReport: [] };
  }
}

const mapFiltersToApi = (filters: ReportsFilters) => {
  return {
    startDate: filters.startDate,
    endDate: filters.endDate,
    departmentId: parseInt(filters.departmentId),
    teamIds: filters.teamId ? [parseInt(filters.teamId)] : undefined,
    agentId: filters.agentId,
    ticketType: filters.ticketType,
    ticketStatuses: filters.ticketStatuses?.map((status) => parseInt(status)),
  };
};

const mapApiTicketReport = (apiTicketReport: any): TicketReport => {
  const resolvedDate = apiTicketReport.resolvedDate ? convertISOStringToDate(`${apiTicketReport.resolvedDate}Z`) : undefined;

  return {
    id: `${apiTicketReport.id}`,
    prettyId: getTicketPrettyId(apiTicketReport.id),
    type: apiTicketReport.type,
    urgency: apiTicketReport.urgency,
    status: apiTicketReport.status,
    categoryName: apiTicketReport.categoryName,
    location: apiTicketReport.location,
    teamName: apiTicketReport.teamName,
    agentName: apiTicketReport.agentName,
    userName: apiTicketReport.userName,
    resolvedDate: resolvedDate ? format(resolvedDate, "dd/MM/yyyy HH:mm") : "",
    slaExceeded: apiTicketReport.slaExceeded,
    totalLoggedTime: formatMinutesToTimeString(apiTicketReport.totalLoggedMinutes ?? 0),
  };
};

const mapAgentFiltersToApi = (filters: AgentCurrentTicketsFilters) => ({
  closedStartDate: filters.closedStartDate,
  closedEndDate: filters.closedEndDate,
  inWorkStatuses: inWorkStatusesIds.map((s) => parseInt(s)),
  closedStatuses: closedStatusesIds.map((s) => parseInt(s)),
});
